import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Download, FileText, FileSpreadsheet, ArrowUpDown, Search, CalendarClock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import SearchableCandidateSelect from '@/components/SearchableCandidateSelect';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Interviews() {
  const [interviews, setInterviews] = useState([]);
  const [resumes, setResumes] = useState([]);
  const [open, setOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkStatus, setBulkStatus] = useState('');
  const [sortField, setSortField] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [searchText, setSearchText] = useState('');
  const [filterCompany, setFilterCompany] = useState('all');
  const [filterRecruiter, setFilterRecruiter] = useState('all');
  const [formData, setFormData] = useState({ resume_id: '', date: '', type: 'phone', status: 'scheduled' });
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({ id: '', date: '', time: '', venue: '', type: '', notes: '' });

  const fetchInterviews = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/interviews`, { headers: { Authorization: `Bearer ${token}` } });
      setInterviews(response.data);
    } catch (error) { toast.error('Failed to fetch interviews'); }
  };

  const fetchResumes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/resumes`, { headers: { Authorization: `Bearer ${token}` } });
      setResumes(response.data);
    } catch (error) { toast.error('Failed to fetch resumes'); }
  };

  useEffect(() => { fetchInterviews(); fetchResumes(); }, []);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.role === 'admin';
  const canExport = user.role === 'admin' || user.role === 'team_leader';

  const handleDeleteInterview = async (id) => {
    if (!window.confirm('Delete this interview?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/interviews/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Interview deleted'); fetchInterviews();
    } catch (error) { toast.error('Failed to delete interview'); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/interviews`, formData, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Interview scheduled'); setOpen(false);
      setFormData({ resume_id: '', date: '', type: 'phone', status: 'scheduled' }); fetchInterviews();
    } catch (error) { toast.error('Failed to schedule interview'); }
  };

  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/interviews/${selectedInterview.id}/status`, { status: newStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Status updated'); setStatusDialogOpen(false); fetchInterviews();
    } catch (error) { toast.error('Failed to update status'); }
  };

  const openReschedule = (interview) => {
    setRescheduleData({ id: interview.id, date: interview.date || '', time: interview.time || '', venue: interview.venue || '', type: interview.type || 'phone', notes: interview.notes || '' });
    setRescheduleOpen(true);
  };

  const handleReschedule = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const { id, ...payload } = rescheduleData;
      await axios.put(`${API}/interviews/${id}/reschedule`, payload, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Interview rescheduled'); setRescheduleOpen(false); fetchInterviews();
    } catch (error) { toast.error('Failed to reschedule'); }
  };

  const handleBulkStatusUpdate = async () => {
    if (!bulkStatus || selectedIds.length === 0) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/interviews/bulk-status`, { ids: selectedIds, status: bulkStatus }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Updated ${selectedIds.length} interview(s)`); setSelectedIds([]); setBulkStatus(''); fetchInterviews();
    } catch { toast.error('Bulk update failed'); }
  };

  const handleExport = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/export/interviews?format=${format}`, { headers: { Authorization: `Bearer ${token}` }, responseType: 'blob' });
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a'); link.href = url; link.setAttribute('download', `interviews.${ext}`);
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (error) { toast.error('Failed to export'); }
  };

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  const toggleAll = () => setSelectedIds(selectedIds.length === filteredInterviews.length ? [] : filteredInterviews.map(i => i.id));

  const toggleSort = (field) => {
    if (sortField === field) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('desc'); }
  };

  const uniqueCompanies = useMemo(() => [...new Set(interviews.map(i => i.client_name).filter(Boolean))].sort(), [interviews]);
  const uniqueRecruiters = useMemo(() => [...new Set(interviews.map(i => i.recruiter_name).filter(Boolean))].sort(), [interviews]);

  const filteredInterviews = useMemo(() => {
    let data = [...interviews];
    if (searchText) {
      const q = searchText.toLowerCase();
      data = data.filter(i => (i.candidate_name || '').toLowerCase().includes(q) || (i.position_title || '').toLowerCase().includes(q) || (i.client_name || '').toLowerCase().includes(q));
    }
    if (filterCompany !== 'all') data = data.filter(i => i.client_name === filterCompany);
    if (filterRecruiter !== 'all') data = data.filter(i => i.recruiter_name === filterRecruiter);
    if (filterStatus !== 'all') data = data.filter(i => i.status === filterStatus);
    if (filterDateFrom) data = data.filter(i => (i.date || '') >= filterDateFrom);
    if (filterDateTo) data = data.filter(i => (i.date || '').slice(0, 10) <= filterDateTo);
    data.sort((a, b) => {
      const valA = a[sortField] || '';
      const valB = b[sortField] || '';
      return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
    });
    return data;
  }, [interviews, searchText, filterCompany, filterRecruiter, filterStatus, filterDateFrom, filterDateTo, sortField, sortDir]);

  const SortHeader = ({ field, children }) => (
    <TableHead className="font-bold text-blue-900 cursor-pointer select-none hover:bg-blue-100" onClick={() => toggleSort(field)}>
      <span className="flex items-center gap-1">{children}<ArrowUpDown size={12} className={sortField === field ? 'text-blue-600' : 'text-slate-400'} /></span>
    </TableHead>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-blue-900 mb-2" data-testid="interviews-title" style={{ fontFamily: 'Manrope, sans-serif' }}>Interviews</h1>
          <p className="text-slate-600">Schedule and track interviews</p>
        </div>
        <div className="flex items-center gap-3">
          {canExport && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button data-testid="export-interviews-button" variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                  <Download size={16} className="mr-2" />Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem data-testid="export-interviews-excel" onClick={() => handleExport('excel')}><FileSpreadsheet size={16} className="mr-2" />Excel</DropdownMenuItem>
                <DropdownMenuItem data-testid="export-interviews-pdf" onClick={() => handleExport('pdf')}><FileText size={16} className="mr-2" />PDF</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-interview-button" className="bg-blue-600 hover:bg-blue-700 text-white rounded font-bold"><Plus size={16} className="mr-2" />Schedule Interview</Button>
            </DialogTrigger>
            <DialogContent className="rounded">
              <DialogHeader><DialogTitle className="text-2xl font-bold text-blue-900">Schedule Interview</DialogTitle></DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Candidate</Label>
                  <select data-testid="interview-resume-select" value={formData.resume_id} onChange={(e) => setFormData({ ...formData, resume_id: e.target.value })} required className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm sr-only">
                    <option value="">Select a candidate</option>
                    {resumes.map(r => <option key={r.id} value={r.id}>{r.candidate_name} - {r.position_title}</option>)}
                  </select>
                  <div className="mt-2">
                    <SearchableCandidateSelect
                      value={formData.resume_id}
                      onChange={(id) => setFormData({ ...formData, resume_id: id })}
                      resumes={resumes}
                      testId="interview-resume"
                    />
                  </div></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Date & Time</Label>
                  <Input data-testid="interview-date-input" type="datetime-local" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} required className="mt-2 rounded border-blue-200" /></div>
                <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Type</Label>
                  <select data-testid="interview-type-select" value={formData.type} onChange={(e) => setFormData({ ...formData, type: e.target.value })} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                    <option value="phone">Phone</option><option value="video">Video</option><option value="onsite">On-site</option>
                  </select></div>
                <Button type="submit" data-testid="submit-interview-button" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Schedule Interview</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-blue-100 rounded shadow-sm p-4 mb-4 flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Search</Label>
          <div className="relative mt-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-interviews" placeholder="Search candidate, position, client..." value={searchText} onChange={(e) => setSearchText(e.target.value)} className="h-9 pl-9 rounded border-blue-200 text-sm" />
          </div>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Company</Label>
          <select data-testid="filter-interview-company" value={filterCompany} onChange={(e) => setFilterCompany(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Companies</option>
            {uniqueCompanies.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Recruiter</Label>
          <select data-testid="filter-interview-recruiter" value={filterRecruiter} onChange={(e) => setFilterRecruiter(e.target.value)} className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Recruiters</option>
            {uniqueRecruiters.map(r => <option key={r} value={r}>{r}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">Status</Label>
          <select data-testid="filter-interview-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="mt-1 flex h-9 rounded border border-blue-200 bg-white px-3 py-1 text-sm">
            <option value="all">All Statuses</option>
            <option value="scheduled">Scheduled</option><option value="rescheduled">Rescheduled</option>
            <option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="no-show">No Show</option>
            <option value="selected">Selected</option><option value="rejected">Rejected</option>
            <option value="l1_scheduled">L1 Scheduled</option><option value="l2_scheduled">L2 Scheduled</option><option value="hr_round">HR Round</option>
          </select>
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">From Date</Label>
          <Input data-testid="filter-interview-from" type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase tracking-wider text-blue-900">To Date</Label>
          <Input data-testid="filter-interview-to" type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="mt-1 h-9 rounded border-blue-200 text-sm" />
        </div>
        <Button variant="ghost" size="sm" onClick={() => { setSearchText(''); setFilterCompany('all'); setFilterRecruiter('all'); setFilterStatus('all'); setFilterDateFrom(''); setFilterDateTo(''); }} className="text-slate-500 text-xs">Clear Filters</Button>
        <span className="text-xs text-slate-500 ml-auto">{filteredInterviews.length} of {interviews.length} interviews</span>
      </div>

      {/* Status update dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="rounded">
          <DialogHeader><DialogTitle className="text-2xl font-bold text-blue-900">Update Interview Status</DialogTitle></DialogHeader>
          {selectedInterview && (
            <form onSubmit={handleStatusUpdate} className="space-y-4 mt-4">
              <div className="bg-blue-50 p-4 rounded border border-blue-200">
                <p className="text-sm font-semibold text-blue-900">{selectedInterview.candidate_name}</p>
                <p className="text-xs text-slate-600">{selectedInterview.position_title}</p>
                <p className="text-xs text-slate-500 mt-1">{new Date(selectedInterview.date).toLocaleString()}</p>
              </div>
              <div><Label className="text-xs font-bold uppercase tracking-wider text-blue-900">New Status</Label>
                <select value={newStatus} onChange={(e) => setNewStatus(e.target.value)} className="mt-2 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm" required>
                  <option value="">Select status</option><option value="scheduled">Scheduled</option><option value="rescheduled">Rescheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="no-show">No Show</option>
                  <option value="selected">Selected</option><option value="rejected">Rejected</option><option value="l1_scheduled">L1 Scheduled</option><option value="l2_scheduled">L2 Scheduled</option><option value="hr_round">HR Round</option>
                </select></div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded font-bold">Update Status</Button>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {selectedIds.length > 0 && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded p-3 flex items-center gap-3" data-testid="bulk-interview-bar">
          <span className="text-sm font-bold text-blue-900">{selectedIds.length} selected</span>
          <select data-testid="bulk-interview-status" value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)} className="h-8 rounded border border-blue-200 bg-white px-2 text-sm">
            <option value="">Set status...</option><option value="scheduled">Scheduled</option><option value="rescheduled">Rescheduled</option><option value="completed">Completed</option><option value="cancelled">Cancelled</option><option value="no-show">No Show</option>
            <option value="selected">Selected</option><option value="rejected">Rejected</option><option value="l1_scheduled">L1 Scheduled</option><option value="l2_scheduled">L2 Scheduled</option><option value="hr_round">HR Round</option>
          </select>
          <Button size="sm" data-testid="bulk-interview-update" onClick={handleBulkStatusUpdate} disabled={!bulkStatus} className="bg-blue-600 hover:bg-blue-700 text-white text-xs rounded font-bold">Apply</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} className="text-slate-600 text-xs">Clear</Button>
        </div>
      )}

      <div className="bg-white border border-blue-100 rounded shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-blue-50 border-b border-blue-100">
              <TableHead className="w-10"><input type="checkbox" data-testid="select-all-interviews" checked={selectedIds.length === filteredInterviews.length && filteredInterviews.length > 0} onChange={toggleAll} className="rounded border-blue-300" /></TableHead>
              <SortHeader field="candidate_name">Candidate</SortHeader>
              <TableHead className="font-bold text-blue-900">Position</TableHead>
              <TableHead className="font-bold text-blue-900">Client</TableHead>
              <SortHeader field="date">Date & Time</SortHeader>
              <TableHead className="font-bold text-blue-900">Venue</TableHead>
              <TableHead className="font-bold text-blue-900">Type</TableHead>
              <SortHeader field="status">Status</SortHeader>
              <TableHead className="font-bold text-blue-900">Recruiter</TableHead>
              <TableHead className="font-bold text-blue-900">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredInterviews.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center text-slate-500 py-8">No interviews found</TableCell></TableRow>
            ) : (
              filteredInterviews.map((interview) => (
                <TableRow key={interview.id} data-testid={`interview-row-${interview.id}`} className={`hover:bg-blue-50 ${selectedIds.includes(interview.id) ? 'bg-blue-50' : ''}`}>
                  <TableCell><input type="checkbox" data-testid={`select-interview-${interview.id}`} checked={selectedIds.includes(interview.id)} onChange={() => toggleSelect(interview.id)} className="rounded border-blue-300" /></TableCell>
                  <TableCell className="font-semibold text-blue-900">{interview.candidate_name}</TableCell>
                  <TableCell className="text-sm">{interview.position_title}</TableCell>
                  <TableCell className="text-sm">{interview.client_name || <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="text-sm">{new Date(interview.date).toLocaleString()}{interview.time && <span className="text-xs text-slate-500 ml-1">{interview.time}</span>}</TableCell>
                  <TableCell className="text-sm">{interview.venue || <span className="text-slate-400">-</span>}</TableCell>
                  <TableCell className="capitalize text-sm">{interview.type}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 text-xs font-bold uppercase rounded ${
                      interview.status === 'completed' ? 'bg-emerald-100 text-emerald-800' : 
                      interview.status === 'selected' ? 'bg-green-100 text-green-800' :
                      interview.status === 'cancelled' ? 'bg-red-100 text-red-800' : 
                      interview.status === 'rejected' ? 'bg-red-200 text-red-900' :
                      interview.status === 'no-show' ? 'bg-slate-100 text-slate-800' : 
                      interview.status === 'l1_scheduled' ? 'bg-blue-100 text-blue-800' :
                      interview.status === 'l2_scheduled' ? 'bg-indigo-100 text-indigo-800' :
                      interview.status === 'hr_round' ? 'bg-purple-100 text-purple-800' :
                      'bg-amber-100 text-amber-800'}`}>{interview.status?.replace('_', ' ')}</span>
                  </TableCell>
                  <TableCell className="text-sm">{interview.recruiter_name}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" data-testid={`reschedule-interview-${interview.id}`} onClick={() => openReschedule(interview)} title="Reschedule" className="text-amber-600 hover:text-amber-900 hover:bg-amber-50"><CalendarClock size={14} /></Button>
                      <Button size="sm" variant="ghost" onClick={() => { setSelectedInterview(interview); setNewStatus(interview.status); setStatusDialogOpen(true); }} className="text-blue-600 hover:text-blue-900 hover:bg-blue-50"><Edit2 size={14} /></Button>
                      {isAdmin && <Button size="sm" variant="ghost" data-testid={`delete-interview-${interview.id}`} onClick={() => handleDeleteInterview(interview.id)} className="text-red-600 hover:text-red-800 hover:bg-red-50"><Trash2 size={14} /></Button>}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Reschedule Dialog */}
      <Dialog open={rescheduleOpen} onOpenChange={setRescheduleOpen}>
        <DialogContent className="rounded max-w-md">
          <DialogHeader><DialogTitle className="text-2xl font-bold text-blue-900">Reschedule Interview</DialogTitle></DialogHeader>
          <form onSubmit={handleReschedule} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase text-blue-900">Date *</Label>
                <Input data-testid="reschedule-date" type="date" value={rescheduleData.date?.split('T')[0] || ''} onChange={e => setRescheduleData({ ...rescheduleData, date: e.target.value })} required className="mt-1 border-blue-200" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase text-blue-900">Time</Label>
                <Input data-testid="reschedule-time" type="time" value={rescheduleData.time || ''} onChange={e => setRescheduleData({ ...rescheduleData, time: e.target.value })} className="mt-1 border-blue-200" />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Venue</Label>
              <Input data-testid="reschedule-venue" value={rescheduleData.venue || ''} onChange={e => setRescheduleData({ ...rescheduleData, venue: e.target.value })} className="mt-1 border-blue-200" placeholder="e.g. Conference Room A / Zoom Link" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Type</Label>
              <select data-testid="reschedule-type" value={rescheduleData.type || 'phone'} onChange={e => setRescheduleData({ ...rescheduleData, type: e.target.value })} className="mt-1 flex h-10 w-full rounded border border-blue-200 bg-white px-3 py-2 text-sm">
                <option value="phone">Phone</option><option value="video">Video</option><option value="in-person">In-Person</option><option value="panel">Panel</option>
              </select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase text-blue-900">Notes</Label>
              <Input data-testid="reschedule-notes" value={rescheduleData.notes || ''} onChange={e => setRescheduleData({ ...rescheduleData, notes: e.target.value })} className="mt-1 border-blue-200" placeholder="Reason for rescheduling..." />
            </div>
            <Button data-testid="confirm-reschedule-btn" type="submit" className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold">Reschedule Interview</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
